import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { PerStyleVariationMapBuilder } from "@faire/retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { isProductOptionInStock } from "@faire/retailer-visitor-shared/lib/productOptions";
import { getSortedProductOptions } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import { logAssert } from "@faire/web--source/common/logging";
import { parseJson } from "@faire/web--source/common/string";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IProductVariation } from "@faire/web-api--source/indigofair/data/IProductVariation";
import { ProductVariationType } from "@faire/web-api--source/indigofair/data/ProductVariationType";
import cloneDeep from "lodash/cloneDeep";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import { useCallback, useMemo, useState } from "react";
var VARIATION_TYPE_HIERARCHY = [
    ProductVariationType.COLOR,
    ProductVariationType.CUSTOM,
    ProductVariationType.SIZE
];
var findVariantIndex = function(variantType) {
    var index = VARIATION_TYPE_HIERARCHY.indexOf(variantType);
    // this probably isn't needed since every variation will have a type
    // but will leave this here in case the backfill on prod isn't complete/safety precautions
    return index === -1 ? 1.5 : index;
};
var getVariationsFromVariationMap = function(variationMap) {
    return Object.entries(variationMap).map(function(param) {
        var _param = _sliced_to_array(param, 2), name = _param[0], data = _param[1];
        return {
            name: name,
            value: data === null || data === void 0 ? void 0 : data.value,
            type: data === null || data === void 0 ? void 0 : data.type
        };
    });
};
var getVariationKey = function(variations) {
    return JSON.stringify(variations === null || variations === void 0 ? void 0 : variations.slice().sort(function(variation1, variation2) {
        logAssert(variation1.name !== undefined && variation2.name !== undefined, "variations always have a name property", {
            data: {
                variation1: variation1,
                variation2: variation2
            }
        });
        if (variation1.name === variation2.name) {
            return 0;
        }
        return variation1.name > variation2.name ? 1 : -1;
    }));
};
var getVariationsFromVariationKey = function(variationKey) {
    try {
        var parsedVariations = parseJson(variationKey);
        if (Array.isArray(parsedVariations)) {
            return parsedVariations;
        }
    } catch (e) {
    // do nothing
    }
    return [];
};
export var useVariantOptionSelectionViewState = function(product, productAddToCartState) {
    var _product_purchase_information_variation_map, _product_purchase_information;
    var _useState = _sliced_to_array(useState(function() {
        var _productAddToCartState_selectedOption_variations, _productAddToCartState_selectedOption;
        var variations = {};
        (_productAddToCartState_selectedOption = productAddToCartState.selectedOption) === null || _productAddToCartState_selectedOption === void 0 ? void 0 : (_productAddToCartState_selectedOption_variations = _productAddToCartState_selectedOption.variations) === null || _productAddToCartState_selectedOption_variations === void 0 ? void 0 : _productAddToCartState_selectedOption_variations.forEach(function(v) {
            return variations[v.name] = {
                value: v.value,
                type: v.type
            };
        });
        return variations;
    }), 2), selectedVariations = _useState[0], setSelectedVariations = _useState[1];
    var options = useMemo(function() {
        return getSortedProductOptions(product);
    }, [
        product
    ]);
    var optionsMapByVariationKey = useMemo(function() {
        return options.reduce(function(optionMap, option) {
            optionMap[getVariationKey(option.variations)] = option;
            return optionMap;
        }, {});
    }, [
        options
    ]);
    var optionBasedOnSelectedVariations = useMemo(function() {
        return optionsMapByVariationKey[getVariationKey(getVariationsFromVariationMap(selectedVariations))];
    }, [
        optionsMapByVariationKey,
        selectedVariations
    ]);
    var updateOption = useCallback(function(variations) {
        var optionBasedOnSelectedVariations = optionsMapByVariationKey[getVariationKey(getVariationsFromVariationMap(variations))];
        productAddToCartState.updateSelectedOption(optionBasedOnSelectedVariations);
    }, [
        optionsMapByVariationKey,
        productAddToCartState
    ]);
    var getSelectedVariationValue = useCallback(function(variationName) {
        var _selectedVariations_variationName;
        return (_selectedVariations_variationName = selectedVariations[variationName]) === null || _selectedVariations_variationName === void 0 ? void 0 : _selectedVariations_variationName.value;
    }, [
        selectedVariations
    ]);
    var hasValidOptionBasedOnSelectedVariations = useMemo(function() {
        return optionBasedOnSelectedVariations !== undefined;
    }, [
        optionBasedOnSelectedVariations
    ]);
    var allVariations = useMemo(function() {
        return uniqBy(flatten(options.map(function(option) {
            var _option_variations;
            return (_option_variations = option.variations) !== null && _option_variations !== void 0 ? _option_variations : [];
        })), function(variation) {
            return JSON.stringify(variation);
        });
    }, [
        options
    ]);
    var variationsGroupedByName = useMemo(function() {
        return groupBy(allVariations, function(param) {
            var name = param.name;
            return name;
        });
    }, [
        allVariations
    ]);
    var variationTypes = useMemo(function() {
        var variationKeysByName = Object.keys(variationsGroupedByName);
        var variationPropsByName = variationKeysByName.map(function(variationKey) {
            var _variationsGroupedByName_variationKey_, _variationsGroupedByName_variationKey;
            var _variationsGroupedByName_variationKey__type;
            return {
                value: variationKey,
                type: (_variationsGroupedByName_variationKey__type = (_variationsGroupedByName_variationKey = variationsGroupedByName[variationKey]) === null || _variationsGroupedByName_variationKey === void 0 ? void 0 : (_variationsGroupedByName_variationKey_ = _variationsGroupedByName_variationKey[0]) === null || _variationsGroupedByName_variationKey_ === void 0 ? void 0 : _variationsGroupedByName_variationKey_.type) !== null && _variationsGroupedByName_variationKey__type !== void 0 ? _variationsGroupedByName_variationKey__type : ProductVariationType.CUSTOM
            };
        });
        return variationPropsByName.sort(function(variationA, variationB) {
            return findVariantIndex(variationA.type) - findVariantIndex(variationB.type);
        });
    }, [
        variationsGroupedByName
    ]);
    var allOptionsHaveSwatches = useCallback(function(variationName) {
        var _product_purchase_information_variation_map_entries_find, _product_purchase_information_variation_map, _product_purchase_information;
        var entryValues = (_product_purchase_information = product.purchase_information) === null || _product_purchase_information === void 0 ? void 0 : (_product_purchase_information_variation_map = _product_purchase_information.variation_map) === null || _product_purchase_information_variation_map === void 0 ? void 0 : (_product_purchase_information_variation_map_entries_find = _product_purchase_information_variation_map.entries.find(function(entry) {
            return entry.name === variationName;
        })) === null || _product_purchase_information_variation_map_entries_find === void 0 ? void 0 : _product_purchase_information_variation_map_entries_find.entry_values;
        var _entryValues_length, _entryValues_every;
        return ((_entryValues_length = entryValues === null || entryValues === void 0 ? void 0 : entryValues.length) !== null && _entryValues_length !== void 0 ? _entryValues_length : 0) > 0 ? (_entryValues_every = entryValues === null || entryValues === void 0 ? void 0 : entryValues.every(function(entryValue) {
            return entryValue.image !== undefined;
        })) !== null && _entryValues_every !== void 0 ? _entryValues_every : false : false;
    }, [
        (_product_purchase_information = product.purchase_information) === null || _product_purchase_information === void 0 ? void 0 : (_product_purchase_information_variation_map = _product_purchase_information.variation_map) === null || _product_purchase_information_variation_map === void 0 ? void 0 : _product_purchase_information_variation_map.entries
    ]);
    var styleVariationTypes = useMemo(function() {
        return variationTypes.filter(function(variation) {
            return variation.type !== ProductVariationType.SIZE;
        });
    }, [
        variationTypes
    ]);
    var isStyleVariationType = useCallback(function(name) {
        return styleVariationTypes.some(function(variationType) {
            return variationType.value === name;
        });
    }, [
        styleVariationTypes
    ]);
    var primaryVariation = useMemo(function() {
        var _variationTypes_;
        var primaryVariationName = (_variationTypes_ = variationTypes[0]) === null || _variationTypes_ === void 0 ? void 0 : _variationTypes_.value;
        if (primaryVariationName === undefined) {
            return undefined;
        }
        var primaryVariationValue = getSelectedVariationValue(primaryVariationName);
        return IProductVariation.build({
            name: primaryVariationName,
            value: primaryVariationValue
        });
    }, [
        getSelectedVariationValue,
        variationTypes
    ]);
    var doesVariationKeyContainVariation = useCallback(function(variationKey, variation) {
        return variationKey.includes(JSON.stringify(variation).slice(1, -1));
    }, []);
    var findAvailableVariationKey = useCallback(function(selectedVariation) {
        var currentPrimaryVariation = primaryVariation;
        if (currentPrimaryVariation === undefined) {
            return undefined;
        }
        // when selected variation is the primary variation,
        // ignore current primary variation and check
        // whether some variation key contains selected variation
        if (selectedVariation.name === currentPrimaryVariation.name) {
            return Object.keys(optionsMapByVariationKey).find(function(variationKey) {
                return doesVariationKeyContainVariation(variationKey, selectedVariation);
            });
        }
        // when selected variation is not the primary variation,
        // apply primary variation and check
        // whether some variation key contains both variations
        return Object.keys(optionsMapByVariationKey).find(function(variationKey) {
            return doesVariationKeyContainVariation(variationKey, selectedVariation) && doesVariationKeyContainVariation(variationKey, currentPrimaryVariation);
        });
    }, [
        doesVariationKeyContainVariation,
        optionsMapByVariationKey,
        primaryVariation
    ]);
    var findInStockVariationKey = useCallback(function(selectedVariation) {
        var currentPrimaryVariation = primaryVariation;
        if (currentPrimaryVariation === undefined) {
            return undefined;
        }
        // when selected variation is the primary variation,
        // ignore current primary variation and check
        // whether some variation key contains selected variation
        // and the option is FOR_SALE
        if (selectedVariation.name === currentPrimaryVariation.name) {
            return Object.keys(optionsMapByVariationKey).find(function(variationKey) {
                var _optionsMapByVariationKey_variationKey;
                return doesVariationKeyContainVariation(variationKey, selectedVariation) && ((_optionsMapByVariationKey_variationKey = optionsMapByVariationKey[variationKey]) === null || _optionsMapByVariationKey_variationKey === void 0 ? void 0 : _optionsMapByVariationKey_variationKey.state) === IProduct.State.FOR_SALE;
            });
        }
        // when selected variation is not the primary variation,
        // apply primary variation and check
        // whether some variation key contains both variations
        // and the option is FOR_SALE
        return Object.keys(optionsMapByVariationKey).find(function(variationKey) {
            var _optionsMapByVariationKey_variationKey;
            return doesVariationKeyContainVariation(variationKey, selectedVariation) && doesVariationKeyContainVariation(variationKey, currentPrimaryVariation) && ((_optionsMapByVariationKey_variationKey = optionsMapByVariationKey[variationKey]) === null || _optionsMapByVariationKey_variationKey === void 0 ? void 0 : _optionsMapByVariationKey_variationKey.state) === IProduct.State.FOR_SALE;
        });
    }, [
        doesVariationKeyContainVariation,
        optionsMapByVariationKey,
        primaryVariation
    ]);
    var isAlternateOpenSizingProduct = useCallback(function(productOption) {
        return productAddToCartState.isOptionPartOfOpenSizing(productOption);
    }, [
        productAddToCartState
    ]);
    var buildAlternateOpenSizingVariationMap = useCallback(function(productOption) {
        if (productOption === undefined || !isAlternateOpenSizingProduct(productOption)) {
            return undefined;
        }
        return PerStyleVariationMapBuilder.buildVariationMap(productOption.token, product);
    }, [
        isAlternateOpenSizingProduct,
        product
    ]);
    /**
     * @param variation the variation to be selected
     * @returns state of selected variation: FOR_SALE when in stock, SALE_PAUSED when out of stock, undefined when unavailable
     */ var stateForAlternateVariation = useCallback(function(variation) {
        if (variation.name === undefined || variation.value === undefined) {
            return undefined;
        }
        if (!isStyleVariationType(variation.name)) {
            var variationMap = cloneDeep(selectedVariations);
            var thisVariation = variationMap[variation.name];
            if (thisVariation) {
                thisVariation.value = variation.value;
            }
            var option = optionsMapByVariationKey[getVariationKey(getVariationsFromVariationMap(variationMap))];
            return option === null || option === void 0 ? void 0 : option.state;
        }
        var availableKey = findAvailableVariationKey(variation);
        if (availableKey === undefined) {
            return undefined;
        }
        var inStockKey = findInStockVariationKey(variation);
        if (inStockKey === undefined) {
            return IProduct.State.SALES_PAUSED;
        }
        return IProduct.State.FOR_SALE;
    }, [
        findAvailableVariationKey,
        findInStockVariationKey,
        isStyleVariationType,
        optionsMapByVariationKey,
        selectedVariations
    ]);
    /**
     * @param variation the variation to be selected
     * @returns an option with specified variation and the primary variation selected, returns undefined if such option does not exist
     */ var optionForAlternateVariation = useCallback(function(variation) {
        if (variation.name === undefined || variation.value === undefined) {
            return undefined;
        }
        if (!isStyleVariationType(variation.name)) {
            var variationMap = cloneDeep(selectedVariations);
            var thisVariation = variationMap[variation.name];
            if (thisVariation) {
                thisVariation.value = variation.value;
            }
            return optionsMapByVariationKey[getVariationKey(getVariationsFromVariationMap(variationMap))];
        }
        var key = findAvailableVariationKey(variation);
        if (key === undefined) {
            return undefined;
        }
        return optionsMapByVariationKey[key];
    }, [
        findAvailableVariationKey,
        isStyleVariationType,
        optionsMapByVariationKey,
        selectedVariations
    ]);
    var isVariationOutOfStock = useCallback(function(variation) {
        var productOption = optionForAlternateVariation(variation);
        var alternateOpenSizingVariationMap = buildAlternateOpenSizingVariationMap(productOption);
        if (productOption && alternateOpenSizingVariationMap) {
            return !isProductOptionInStock(productOption, alternateOpenSizingVariationMap);
        } else {
            return stateForAlternateVariation(variation) !== IProduct.State.FOR_SALE;
        }
    }, [
        buildAlternateOpenSizingVariationMap,
        optionForAlternateVariation,
        stateForAlternateVariation
    ]);
    var handleVariationSelect = useCallback(function(name, value) {
        var newSelectedVariations = selectedVariations;
        var variation = selectedVariations[name];
        if (variation) {
            newSelectedVariations = _object_spread_props(_object_spread({}, selectedVariations), _define_property({}, name, _object_spread_props(_object_spread({}, variation), {
                value: value
            })));
        }
        var optionBasedOnSelectedVariations = optionsMapByVariationKey[getVariationKey(getVariationsFromVariationMap(newSelectedVariations))];
        if (optionBasedOnSelectedVariations === undefined) {
            // when selected variations has no available options,
            // switch to an available option with primary variation unchanged
            var variationKey = findAvailableVariationKey(IProductVariation.build({
                name: name,
                value: value
            }));
            var variations = getVariationsFromVariationKey(variationKey !== null && variationKey !== void 0 ? variationKey : getVariationKey(getVariationsFromVariationMap(newSelectedVariations)));
            variations === null || variations === void 0 ? void 0 : variations.forEach(function(variation) {
                if (variation.name !== undefined && variation.type !== undefined) {
                    newSelectedVariations[variation.name] = {
                        value: variation.value,
                        type: variation.type
                    };
                }
            });
        }
        setSelectedVariations(newSelectedVariations);
        updateOption(newSelectedVariations);
    }, [
        findAvailableVariationKey,
        optionsMapByVariationKey,
        selectedVariations,
        updateOption
    ]);
    var isAlternateOpenSizingAvailable = useCallback(function(productOption) {
        var variationMap = buildAlternateOpenSizingVariationMap(productOption);
        return !!(variationMap === null || variationMap === void 0 ? void 0 : variationMap.some(function(variation) {
            return variation.inventory > 0;
        }));
    }, [
        buildAlternateOpenSizingVariationMap
    ]);
    return useMemo(function() {
        return {
            product: product,
            getSelectedVariationValue: getSelectedVariationValue,
            hasValidOptionBasedOnSelectedVariations: hasValidOptionBasedOnSelectedVariations,
            variationsGroupedByName: variationsGroupedByName,
            variationTypes: variationTypes,
            allOptionsHaveSwatches: allOptionsHaveSwatches,
            isAlternateOpenSizingProduct: isAlternateOpenSizingProduct,
            buildAlternateOpenSizingVariationMap: buildAlternateOpenSizingVariationMap,
            isVariationOutOfStock: isVariationOutOfStock,
            handleVariationSelect: handleVariationSelect,
            currentStyleHasOnlyPrePacks: productAddToCartState.currentStyleHasOnlyPrePacks,
            styleVariationTypes: styleVariationTypes,
            isAlternateOpenSizingAvailable: isAlternateOpenSizingAvailable,
            stateForAlternateVariation: stateForAlternateVariation,
            optionForAlternateVariation: optionForAlternateVariation
        };
    }, [
        product,
        getSelectedVariationValue,
        hasValidOptionBasedOnSelectedVariations,
        variationsGroupedByName,
        variationTypes,
        allOptionsHaveSwatches,
        isAlternateOpenSizingProduct,
        buildAlternateOpenSizingVariationMap,
        isVariationOutOfStock,
        handleVariationSelect,
        productAddToCartState.currentStyleHasOnlyPrePacks,
        styleVariationTypes,
        isAlternateOpenSizingAvailable,
        stateForAlternateVariation,
        optionForAlternateVariation
    ]);
};
